import { useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { X, ImagePlus, Loader2, ArrowUpFromLine } from "lucide-react";
import { cn } from "@/lib/utils";
import { UnsupportedFileModal, checkImageFile, IMAGE_ACCEPT, type UnsupportedReason } from "@/components/ui/unsupported-file-modal";

interface UploadZoneProps {
  imageUrl: string | null;
  onImageChange: (url: string | null, meta?: { width: number; height: number }) => void;
}

export function UploadZone({ imageUrl, onImageChange }: UploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [unsupported, setUnsupported] = useState<UnsupportedReason | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = useCallback(
    async (file: File) => {
      // Refused before uploading, so the user isn't charged or kept waiting.
      const reason = checkImageFile(file);
      if (reason) { setUnsupported(reason); return; }
      setIsUploading(true);
      try {
        const dims = await new Promise<{ width: number; height: number }>((resolve, reject) => {
          const img = new Image();
          const objUrl = URL.createObjectURL(file);
          img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
            URL.revokeObjectURL(objUrl);
          };
          img.onerror = () => {
            URL.revokeObjectURL(objUrl);
            reject(new Error("Failed to read image"));
          };
          img.src = objUrl;
        }).catch(() => null);

        const formData = new FormData();
        formData.append("file", file);
        const result = await api.upload<{ success: boolean; url: string }>(
          "/upload",
          formData,
        );
        onImageChange(result.url, dims ?? undefined);
      } catch (err) {
        console.error("Upload failed:", err);
      } finally {
        setIsUploading(false);
      }
    },
    [onImageChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) uploadFile(file);
    },
    [uploadFile],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = ""; // so picking the same file again still fires
      if (file) uploadFile(file);
    },
    [uploadFile],
  );

  if (imageUrl) {
    return (
      <div>
        <label className="block text-xs font-semibold text-[#8a8570] uppercase tracking-wider mb-2">
          Reference Image
        </label>
        <div className="relative rounded-xl overflow-hidden border border-white/50 group">
          <img
            src={imageUrl}
            alt="Reference"
            className="w-full max-h-72 object-contain bg-[#f0ebe0]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <button
            onClick={() => onImageChange(null)}
            className="absolute top-3 right-3 p-1.5 rounded-lg bg-black/40 backdrop-blur-sm text-white/80 hover:text-white hover:bg-black/60 transition-all border border-white/20"
          >
            <X className="size-4" />
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute bottom-3 right-3 px-3 py-1.5 rounded-lg bg-black/40 backdrop-blur-sm text-white/80 hover:text-white hover:bg-black/60 transition-all border border-white/20 text-xs font-medium md:opacity-0 md:group-hover:opacity-100"
          >
            Replace
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
        <UnsupportedFileModal reason={unsupported} onClose={() => setUnsupported(null)} onChooseAnother={() => fileInputRef.current?.click()} />
      </div>
    );
  }

  return (
    <div>
      <label className="block text-xs font-semibold text-[#8a8570] uppercase tracking-wider mb-2">
        Reference Image
      </label>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          "relative flex flex-col items-center justify-center py-14 px-6 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-300",
          isDragging
            ? "border-orange-400/50 bg-orange-50 shadow-[0_0_40px_rgba(232,130,46,0.06)]"
            : "border-[rgba(45,49,39,0.12)] bg-white hover:border-[rgba(45,49,39,0.18)] hover:bg-[rgba(45,49,39,0.02)]",
        )}
      >
        {isUploading ? (
          <div className="flex flex-col items-center">
            <div className="size-12 rounded-2xl bg-orange-50 flex items-center justify-center mb-3 animate-pulse">
              <Loader2 className="size-5 animate-spin text-[#e8822e]" />
            </div>
            <p className="text-sm text-[#8a8570] font-medium">Uploading...</p>
          </div>
        ) : (
          <>
            <div className="size-12 rounded-2xl bg-white/40 border border-white/50 flex items-center justify-center mb-3 transition-colors">
              <ImagePlus className="size-5 text-[#b5b0a0]" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-[#5a5545] font-medium mb-1">
              Drop your image here
            </p>
            <p className="text-xs text-[#b5b0a0]">
              or click to browse · JPG, PNG, WEBP, HEIC
            </p>
            <div className="flex items-center gap-1.5 mt-3 text-[10px] text-orange-400/60 font-medium">
              <ArrowUpFromLine className="size-3" />
              Up to 50MB
            </div>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
      <UnsupportedFileModal reason={unsupported} onClose={() => setUnsupported(null)} onChooseAnother={() => fileInputRef.current?.click()} />
    </div>
  );
}
